import type { AgentConnectivityState } from '@reflex/shared';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { Spinner } from '@reflex/ui/components/shared/Spinner';

interface ConnectivityBannerProps {
  state: AgentConnectivityState;
  since?: number;
  reason?: string;
  className?: string;
}

interface BannerMeta {
  iconClass: string;
  containerClass: string;
  title: string;
  description: string;
  spin?: boolean;
}

const META: Record<Exclude<AgentConnectivityState, 'live'>, BannerMeta> = {
  reconnecting: {
    iconClass: 'text-status-accent-amber',
    containerClass: 'border-status-accent-amber/30 bg-status-accent-amber/10',
    title: 'Reconnecting to devbox…',
    description: 'Messages sent now will be queued and delivered when the connection recovers.',
    spin: true,
  },
  degraded: {
    iconClass: 'text-status-error',
    containerClass: 'border-status-error/30 bg-status-error/10',
    title: 'Connection degraded',
    description: 'Live streaming paused. Messages are queued and will deliver on reconnect.',
  },
};

/**
 * Inline banner shown above the chat input while the server-side Axon
 * connection for this agent is not `live`. Hidden entirely in the `live`
 * state — callers should gate rendering on `state !== 'live'`.
 */
export function ConnectivityBanner({ state, reason, className }: ConnectivityBannerProps) {
  if (state === 'live') return null;
  const meta = META[state];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'mx-auto flex w-full max-w-3xl items-start gap-2 border-t px-4 py-2 text-xs',
        meta.containerClass,
        className,
      )}
      data-testid="connectivity-banner"
    >
      {meta.spin ? (
        <Spinner size="sm" className={cn('mt-0.5', meta.iconClass)} />
      ) : (
        <AlertTriangle className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', meta.iconClass)} />
      )}
      <div className="min-w-0 flex-1">
        <div className="font-medium text-foreground">{meta.title}</div>
        <div className="text-muted-foreground">{meta.description}</div>
        {reason && <div className="mt-0.5 truncate text-muted-foreground/80">{reason}</div>}
      </div>
    </div>
  );
}
